"use client";

import { Fragment, useState, useRef, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import type { CashflowCategory, CashflowSummary, CategoryItem } from "@/types";
import { useCashflowContext } from "./CashflowContext";
import { useForecast } from "@/hooks/useForecast";
import { useForecastProjection } from "@/hooks/useForecastProjection";
import { useCategories } from "@/hooks/useCategories";
import { getProjectionMonthKeys } from "./projectionMonths";
import { buildParentCategoryGroups } from "@/utils/categoryParentGroups";

const FORECAST_MONTHS = 6;
const HISTORY_MONTHS = 6;

function categoryHasActivityInMonths(
  row: CashflowCategory,
  monthKeys: string[],
): boolean {
  if (monthKeys.length === 0) return true;
  return monthKeys.some((m) => Number(row.totals[m] ?? 0) !== 0);
}

function fmt(n: number | null | undefined, dashZero = true): string {
  if (n === null || n === undefined) return "–";
  if (dashZero && n === 0) return "–";
  return n.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function monthLabel(ym: string): string {
  const [year, month] = ym.split("-");
  return new Date(Number(year), Number(month) - 1).toLocaleString("en-US", {
    month: "short",
    year: "2-digit",
  });
}

function ChevronToggle({ expanded }: { expanded: boolean }) {
  return (
    <svg
      className={`size-4 shrink-0 text-slate-500 transition-transform ${expanded ? "" : "-rotate-90"}`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      aria-hidden
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 9l-7 7-7-7"
      />
    </svg>
  );
}

function ForecastCell({
  month,
  rowKey,
  getValue,
  setValue,
}: {
  month: string;
  rowKey: string;
  getValue: (m: string, k: string) => number | null;
  setValue: (m: string, k: string, v: number | null) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const current = getValue(month, rowKey);

  useEffect(() => {
    if (editing) {
      setDraft(current !== null ? String(current) : "");
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing, current]);

  function commit() {
    const num = parseFloat(draft.replace(/,/g, ""));
    setValue(month, rowKey, Number.isNaN(num) ? null : num);
    setEditing(false);
  }

  if (editing) {
    return (
      <input
        ref={inputRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          if (e.key === "Escape") setEditing(false);
        }}
        className="w-full rounded border border-blue-200 bg-white px-1 py-0.5 text-right text-sm font-normal tabular-nums text-blue-600 outline-none ring-blue-400 focus:ring-2"
      />
    );
  }

  const display =
    current !== null && current !== undefined ? fmt(current, false) : "–";

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className="w-full text-right text-sm font-normal tabular-nums text-blue-600 hover:bg-blue-50/60 rounded px-1"
      title="Click to edit projection"
    >
      {display}
    </button>
  );
}

function NetDisplay({
  value,
  projected,
}: {
  value: number;
  projected?: boolean;
}) {
  const abs = fmt(Math.abs(value), false);
  if (projected) {
    if (value === 0) {
      return <span className="font-bold text-blue-600">–</span>;
    }
    const sign = value > 0 ? "+" : "−";
    return (
      <span className="font-bold text-blue-600">
        {sign}
        {abs}
      </span>
    );
  }
  if (value > 0) {
    return <span className="font-bold text-emerald-700">+{abs}</span>;
  }
  if (value < 0) {
    return <span className="font-bold text-red-700">−{abs}</span>;
  }
  return <span className="font-bold text-slate-400">–</span>;
}

const LABEL_STICKY_BASE =
  "sticky left-0 z-10 border-r border-slate-100 py-2.5 pl-4 pr-3 text-left text-sm font-semibold text-slate-700 whitespace-nowrap";
const LABEL_STICKY = `${LABEL_STICKY_BASE} bg-white`;
const LABEL_STICKY_TOTAL = `${LABEL_STICKY_BASE} bg-slate-100`;
const CELL_HIST =
  "px-3 py-2.5 text-right text-sm tabular-nums whitespace-nowrap";
const CELL_PROJECT_BASE_INNER =
  "px-3 py-2.5 text-right text-sm tabular-nums whitespace-nowrap border-b border-slate-100";
const CELL_PROJECT_BASE = `${CELL_PROJECT_BASE_INNER} bg-slate-50/60`;
const CELL_PROJECT_TOTAL = `${CELL_PROJECT_BASE_INNER} bg-slate-100`;
const TOTAL_ROW_BG = "bg-slate-100";

type GroupedCashflowCategory = {
  parent: CashflowCategory;
  parentMeta?: CategoryItem;
  children: CashflowCategory[];
  syntheticParent: boolean;
};

type TableProps = {
  summary?: CashflowSummary;
  isLoading?: boolean;
  suppressContextSummary?: boolean;
};

export function CashflowTable({
  summary: summaryProp,
  isLoading: isLoadingProp,
  suppressContextSummary,
}: TableProps = {}) {
  const ctx = useCashflowContext();
  const summary = suppressContextSummary
    ? summaryProp
    : (summaryProp ?? ctx.summary);
  const isLoading = isLoadingProp ?? ctx.isLoading;
  const { projectionScenario, setProjectionScenario, effectiveConsolidated } =
    ctx;
  const { getValue, setValue, isLoading: forecastLoading } = useForecast();
  const { categories } = useCategories();
  const projection = useForecastProjection("mid", ctx.currency, {
    horizonMonths: FORECAST_MONTHS,
    lastHistoricalMonth:
      summary?.months && summary.months.length > 0
        ? summary.months[summary.months.length - 1]
        : null,
    scenario: projectionScenario,
    setScenario: setProjectionScenario,
    consolidated: effectiveConsolidated,
  });

  const [incomeOpen, setIncomeOpen] = useState(true);
  const [expensesOpen, setExpensesOpen] = useState(true);
  const [openCategoryGroups, setOpenCategoryGroups] = useState<
    Record<string, boolean>
  >({});
  const [savedFlash, setSavedFlash] = useState(false);

  function handleSaveClick() {
    setSavedFlash(true);
    window.setTimeout(() => setSavedFlash(false), 2000);
  }

  function isGroupOpen(groupKey: string): boolean {
    return openCategoryGroups[groupKey] ?? true;
  }

  function setGroupOpen(groupKey: string, next: boolean): void {
    setOpenCategoryGroups((prev) => ({ ...prev, [groupKey]: next }));
  }

  const historicalMonthKeysForCategoryFilter = useMemo(
    () => summary?.months?.slice(-HISTORY_MONTHS) ?? [],
    [summary?.months],
  );

  const visibleIncomeCategories = useMemo((): CashflowCategory[] => {
    const rows = summary?.incomeCategories ?? [];
    return rows.filter((row) =>
      categoryHasActivityInMonths(row, historicalMonthKeysForCategoryFilter),
    );
  }, [summary?.incomeCategories, historicalMonthKeysForCategoryFilter]);

  const visibleExpenseCategories = useMemo((): CashflowCategory[] => {
    const rows = summary?.expenseCategories ?? [];
    return rows.filter((row) =>
      categoryHasActivityInMonths(row, historicalMonthKeysForCategoryFilter),
    );
  }, [summary?.expenseCategories, historicalMonthKeysForCategoryFilter]);

  const projectedValues = useMemo(() => {
    const byMonth = new Map<string, Map<string, number>>();
    for (const point of projection.data?.points ?? []) {
      const monthMap = byMonth.get(point.month) ?? new Map<string, number>();
      monthMap.set("income:total", Number(point.inflows ?? 0));
      monthMap.set("expenses:total", Number(point.outflows ?? 0));
      for (const row of point.categoryBreakdown ?? []) {
        const rowKey =
          row.categoryType === "income"
            ? `income:${row.categoryId}`
            : row.categoryId;
        monthMap.set(
          rowKey,
          (monthMap.get(rowKey) ?? 0) + Number(row.amount ?? 0),
        );
      }
      byMonth.set(point.month, monthMap);
    }
    return byMonth;
  }, [projection.data?.points]);

  if (isLoading) {
    return (
      <section className="rounded-xl border border-slate-200 bg-white p-8">
        <div className="animate-pulse space-y-3">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="h-8 rounded bg-slate-100" />
          ))}
        </div>
      </section>
    );
  }

  if (!summary || summary.months.length === 0) {
    return (
      <section className="rounded-xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-400">
        No cashflow data in this range. Upload transactions or widen your date
        filters.
      </section>
    );
  }

  const {
    months,
    income,
    totalExpensesPerMonth,
    beginningBalances,
    endingBalances,
    periods,
  } = summary;
  const incomeCategories = visibleIncomeCategories;
  const expenseCategories = visibleExpenseCategories;
  const displayMonths = months.slice(-HISTORY_MONTHS);

  const netByMonth = Object.fromEntries(periods.map((p) => [p.month, p.net]));
  const lastMonth = displayMonths[displayMonths.length - 1];
  const forecastMonths = getProjectionMonthKeys(lastMonth, FORECAST_MONTHS);

  function getEffectiveValue(month: string, rowKey: string): number | null {
    const manual = getValue(month, rowKey);
    if (manual !== null) return manual;
    return projectedValues.get(month)?.get(rowKey) ?? null;
  }

  const forecastBeginning: Record<string, number> = {};
  const forecastEnding: Record<string, number> = {};
  let runningForecast = endingBalances[lastMonth] ?? 0;
  for (const fm of forecastMonths) {
    forecastBeginning[fm] = runningForecast;
    const inc =
      getEffectiveValue(fm, "income:total") ??
      incomeCategories.reduce(
        (s, cat) => s + (getEffectiveValue(fm, `income:${cat.id}`) ?? 0),
        0,
      );
    const exp =
      getEffectiveValue(fm, "expenses:total") ??
      expenseCategories.reduce(
        (s, cat) => s + (getEffectiveValue(fm, cat.id) ?? 0),
        0,
      );
    runningForecast = runningForecast + inc - exp;
    forecastEnding[fm] = runningForecast;
  }

  function forecastNet(month: string): number {
    const inc =
      getEffectiveValue(month, "income:total") ??
      incomeCategories.reduce(
        (s, cat) => s + (getEffectiveValue(month, `income:${cat.id}`) ?? 0),
        0,
      );
    const exp =
      getEffectiveValue(month, "expenses:total") ??
      expenseCategories.reduce(
        (s, cat) => s + (getEffectiveValue(month, cat.id) ?? 0),
        0,
      );
    return inc - exp;
  }

  const categoriesById = new Map(
    categories.map((category) => [category.id, category]),
  );

  function buildCategoryGroups(
    rows: CashflowCategory[],
    type: "income" | "expense",
  ): GroupedCashflowCategory[] {
    return buildParentCategoryGroups(
      rows,
      categoriesById,
      type,
      (row) => row.id,
      (parentMeta) => ({
        id: parentMeta.id,
        name: parentMeta.name,
        color: parentMeta.color,
        totals: {},
      }),
    );
  }

  function renderCategoryGroups(
    groups: GroupedCashflowCategory[],
    type: "income" | "expense",
  ) {
    const valueClass = type === "income" ? "text-emerald-700" : "text-red-700";
    const rowKeyForCategory = (categoryId: string) =>
      type === "income" ? `income:${categoryId}` : categoryId;

    function collapsedHistoricalTotal(
      group: GroupedCashflowCategory,
      month: string,
    ): number {
      const childSum = group.children.reduce(
        (s, c) => s + Number(c.totals[month] ?? 0),
        0,
      );
      const parentOwn = Number(group.parent.totals[month] ?? 0);
      return childSum + parentOwn;
    }

    function collapsedForecastTotal(
      group: GroupedCashflowCategory,
      month: string,
    ): number {
      const childSum = group.children.reduce(
        (s, child) =>
          s + (getEffectiveValue(month, rowKeyForCategory(child.id)) ?? 0),
        0,
      );
      if (group.syntheticParent) return childSum;
      const parentOwn =
        getEffectiveValue(month, rowKeyForCategory(group.parent.id)) ?? 0;
      return childSum + parentOwn;
    }

    return groups.map((group) => {
      const groupKey = `${type}:${group.parent.id}`;
      const isOpen = isGroupOpen(groupKey);
      const hasChildren = group.children.length > 0;
      const showCollapsedRollup = hasChildren && !isOpen;

      return (
        <Fragment key={group.parent.id}>
          <tr key={group.parent.id} className="bg-white">
            <td className={`${LABEL_STICKY} py-2.5 pl-10`}>
              <span className="flex items-center gap-2 font-normal text-slate-600">
                {hasChildren ? (
                  <button
                    type="button"
                    onClick={() => setGroupOpen(groupKey, !isOpen)}
                    className="inline-flex items-center"
                    aria-expanded={isOpen}
                    title="Toggle subcategories"
                  >
                    <ChevronToggle expanded={isOpen} />
                  </button>
                ) : (
                  <span className="inline-block w-4" aria-hidden />
                )}
                <span
                  className="h-2 w-2 shrink-0 rounded-full"
                  style={{ backgroundColor: group.parent.color }}
                />
                {group.parent.name}
              </span>
            </td>
            {displayMonths.map((m, i) => {
              const histAmount = showCollapsedRollup
                ? collapsedHistoricalTotal(group, m)
                : Number(group.parent.totals[m] ?? 0);
              return (
                <td
                  key={m}
                  className={`${CELL_HIST} ${valueClass} ${i === displayMonths.length - 1 ? "border-r-2 border-slate-400" : ""}`}
                >
                  {histAmount ? fmt(histAmount, false) : "–"}
                </td>
              );
            })}
            {forecastMonths.map((m) => {
              const collapsedFcTotal = showCollapsedRollup
                ? collapsedForecastTotal(group, m)
                : null;
              return (
                <td
                  key={m}
                  className={`${CELL_PROJECT_BASE} ${projection.isLoading ? "opacity-70" : ""}`}
                >
                  {showCollapsedRollup ? (
                    <div className="text-right text-sm font-normal tabular-nums text-blue-600">
                      {collapsedFcTotal ? fmt(collapsedFcTotal, false) : "–"}
                    </div>
                  ) : group.syntheticParent ? (
                    <span className="text-slate-400">–</span>
                  ) : (
                    <ForecastCell
                      month={m}
                      rowKey={rowKeyForCategory(group.parent.id)}
                      getValue={getEffectiveValue}
                      setValue={setValue}
                    />
                  )}
                </td>
              );
            })}
          </tr>
          {hasChildren && isOpen
            ? group.children.map((child) => (
                <tr key={child.id} className="bg-white">
                  <td className={`${LABEL_STICKY} py-2.5 pl-14`}>
                    <span className="flex items-center gap-2 font-normal text-slate-600">
                      <span className="inline-block w-4" aria-hidden />
                      <span
                        className="h-2 w-2 shrink-0 rounded-full"
                        style={{ backgroundColor: child.color }}
                      />
                      {child.name}
                    </span>
                  </td>
                  {displayMonths.map((m, i) => (
                    <td
                      key={m}
                      className={`${CELL_HIST} ${valueClass} ${i === displayMonths.length - 1 ? "border-r-2 border-slate-400" : ""}`}
                    >
                      {child.totals[m] ? fmt(child.totals[m], false) : "–"}
                    </td>
                  ))}
                  {forecastMonths.map((m) => (
                    <td
                      key={m}
                      className={`${CELL_PROJECT_BASE} ${projection.isLoading ? "opacity-70" : ""}`}
                    >
                      <ForecastCell
                        month={m}
                        rowKey={rowKeyForCategory(child.id)}
                        getValue={getEffectiveValue}
                        setValue={setValue}
                      />
                    </td>
                  ))}
                </tr>
              ))
            : null}
        </Fragment>
      );
    });
  }

  const thHist =
    "px-3 py-3 text-right text-xs font-semibold uppercase tracking-wide text-slate-500";
  const thProj =
    "px-3 py-3 text-right text-xs font-semibold uppercase tracking-wide text-blue-600";

  return (
    <section className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <header className="border-b border-slate-200 px-4 py-3">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="flex-1 min-w-[200px] text-base font-semibold leading-6 text-[#0f172a]">
            Cashflow &amp; Projections
          </h2>

          <button
            type="button"
            onClick={handleSaveClick}
            className="rounded-lg border border-black/10 bg-white px-3 py-1.5 text-sm font-medium text-[#0f172a] shadow-sm hover:bg-slate-50"
          >
            Save Changes
          </button>
        </div>
        <p className="mt-2 text-xs leading-snug text-slate-500">
          Projection values are saved to your account and reused in Scenario
          Planner.
          {forecastLoading ? " Loading saved forecast values..." : ""}
          {projection.isLoading ? " Loading scenario projection..." : ""}
        </p>
      </header>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] border-collapse">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th
                className={`${LABEL_STICKY} min-w-[200px] bg-slate-50 py-3 pl-8 pr-4 text-left text-xs font-semibold uppercase tracking-wide text-slate-500`}
              >
                &nbsp;
              </th>
              {displayMonths.map((m, i) => (
                <th
                  key={m}
                  className={`${thHist} ${i === displayMonths.length - 1 ? "border-r-2 border-slate-400" : ""}`}
                >
                  {monthLabel(m)}
                </th>
              ))}
              {forecastMonths.map((m) => (
                <th key={m} className={thProj}>
                  <span className="inline-flex items-center justify-end gap-1">
                    {monthLabel(m)}
                    <span
                      className="text-[10px] leading-none text-blue-500"
                      aria-hidden
                    >
                      ☆
                    </span>
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Beginning balance */}
            <tr>
              <td className={`${LABEL_STICKY} py-2.5 pl-8`}>
                Beginning Balance
              </td>
              {displayMonths.map((m, i) => (
                <td
                  key={m}
                  className={`${CELL_HIST} font-semibold text-slate-600 ${i === displayMonths.length - 1 ? "border-r-2 border-slate-400" : ""}`}
                >
                  {fmt(beginningBalances[m])}
                </td>
              ))}
              {forecastMonths.map((m) => (
                <td
                  key={m}
                  className={`${CELL_PROJECT_BASE} font-semibold text-blue-600`}
                >
                  {fmt(forecastBeginning[m], false)}
                </td>
              ))}
            </tr>

            <tr className={TOTAL_ROW_BG}>
              <td className={`${LABEL_STICKY_TOTAL} py-2.5 pl-2`}>
                <button
                  type="button"
                  onClick={() => setIncomeOpen((o) => !o)}
                  className="flex w-full items-center gap-2 text-left"
                  aria-expanded={incomeOpen}
                >
                  <ChevronToggle expanded={incomeOpen} />
                  <span className="font-semibold text-slate-700">
                    Total Cash In
                  </span>
                </button>
              </td>
              {displayMonths.map((m, i) => (
                <td
                  key={m}
                  className={`${CELL_HIST} font-normal text-emerald-700 ${i === displayMonths.length - 1 ? "border-r-2 border-slate-400" : ""}`}
                >
                  {income[m] ? fmt(income[m], false) : "–"}
                </td>
              ))}
              {forecastMonths.map((m) => (
                <td
                  key={m}
                  className={`${CELL_PROJECT_TOTAL} ${projection.isLoading ? "opacity-70" : ""}`}
                >
                  <ForecastCell
                    month={m}
                    rowKey="income:total"
                    getValue={getEffectiveValue}
                    setValue={setValue}
                  />
                </td>
              ))}
            </tr>

            {incomeOpen
              ? renderCategoryGroups(
                  buildCategoryGroups(incomeCategories, "income"),
                  "income",
                )
              : null}

            <tr className={TOTAL_ROW_BG}>
              <td className={`${LABEL_STICKY_TOTAL} py-2.5 pl-2`}>
                <button
                  type="button"
                  onClick={() => setExpensesOpen((o) => !o)}
                  className="flex w-full items-center gap-2 text-left"
                  aria-expanded={expensesOpen}
                >
                  <ChevronToggle expanded={expensesOpen} />
                  <span className="font-semibold text-slate-700">Cash Out</span>
                </button>
              </td>
              {displayMonths.map((m, i) => (
                <td
                  key={m}
                  className={`${CELL_HIST} font-normal text-red-700 ${i === displayMonths.length - 1 ? "border-r-2 border-slate-400" : ""}`}
                >
                  {totalExpensesPerMonth[m]
                    ? fmt(totalExpensesPerMonth[m], false)
                    : "–"}
                </td>
              ))}
              {forecastMonths.map((m) => (
                <td
                  key={m}
                  className={`${CELL_PROJECT_TOTAL} ${projection.isLoading ? "opacity-70" : ""}`}
                >
                  <ForecastCell
                    month={m}
                    rowKey="expense:total"
                    getValue={getEffectiveValue}
                    setValue={setValue}
                  />
                </td>
              ))}
            </tr>

            {expensesOpen
              ? renderCategoryGroups(
                  buildCategoryGroups(expenseCategories, "expense"),
                  "expense",
                )
              : null}

            {/* Net cashflow */}
            <tr className={TOTAL_ROW_BG}>
              <td className={`${LABEL_STICKY_TOTAL} py-2.5 pl-8`}>
                Net Cashflow
              </td>
              {displayMonths.map((m, i) => (
                <td
                  key={m}
                  className={`${CELL_HIST} ${i === displayMonths.length - 1 ? "border-r-2 border-slate-400" : ""}`}
                >
                  <NetDisplay value={netByMonth[m] ?? 0} />
                </td>
              ))}
              {forecastMonths.map((m) => (
                <td key={m} className={CELL_PROJECT_TOTAL}>
                  <div className="text-right">
                    <NetDisplay value={forecastNet(m)} projected />
                  </div>
                </td>
              ))}
            </tr>

            {/* Ending balance */}
            <tr className="border-t border-slate-200">
              <td className={`${LABEL_STICKY} py-2.5 pl-8 font-semibold`}>
                Ending Balance
              </td>
              {displayMonths.map((m, i) => (
                <td
                  key={m}
                  className={`${CELL_HIST} font-bold text-slate-600 ${i === displayMonths.length - 1 ? "border-r-2 border-slate-400" : ""}`}
                >
                  {fmt(endingBalances[m], false)}
                </td>
              ))}
              {forecastMonths.map((m) => (
                <td
                  key={m}
                  className={`${CELL_PROJECT_BASE} font-bold text-blue-600`}
                >
                  {fmt(forecastEnding[m], false)}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
      {savedFlash && (
        <div
          role="status"
          aria-live="polite"
          className="pointer-events-none fixed bottom-5 right-5 z-50 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-700 shadow-md"
        >
          Forecast changes saved
        </div>
      )}
    </section>
  );
}
